/**
 * Journée FISCALE à bascule paramétrable — SPEC retail §9.4bis, CONFORMITE §2.1.
 * UTILITÉ : attribuer chaque pièce à sa journée de vente quand la journée ne suit pas le jour
 * calendaire (nocturnes retail fermant après minuit, CHR) : la journée fiscale court de l'heure
 * de bascule H à H+24h — une pièce scellée AVANT la bascule appartient à la journée de la VEILLE.
 *
 * CONFORME NF525 (contrôlé 2026-07-18) : le référentiel R13 (Grands Totaux, note [4]) exige une
 * période « identifiée sans équivoque », PAS le jour calendaire. Convention retenue : l'identifiant
 * de la journée fiscale = date calendaire du DÉBUT de sa fenêtre (bascule 02:00, fenêtre
 * [02:00 18/07 → 02:00 19/07) → journée « 20260718 »).
 *
 * ⚠️ La bascule est une heure LOCALE, comparée à des horodatages fiscaux LOCAUX (AAAAMMJJHHMMSS,
 * heure de confiance §30.6) — une seule référence horaire de bout en bout, aucun passage UTC :
 * mélanger les référentiels ferait changer une pièce de journée par erreur de fuseau.
 * ⚠️ Le paramètre (BO-driven `fiscal.day_cutoff`, défauts retail 00:00 / CHR 02:00) est INJECTÉ :
 * le cœur ne lit aucun réglage. Un changement de bascule en cours de journée re-attribue
 * conceptuellement les pièces déjà scellées — à faire juste après une clôture (règle BO) ; les
 * gardes `lastClosedDay` de `pendingClosings` garantissent quoi qu'il arrive l'unicité du GTJ.
 */

import { nextDay } from './grand-totals';

const YMDHMS_RE = /^\d{14}$/;
const DAY_RE = /^\d{8}$/;
const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** 'HH:MM' → minutes depuis minuit (0..1439). Lève sur format invalide (fail-fast, §23.0). */
export function parseDayCutoff(hhmm: string): number {
  const m = HHMM_RE.exec(hhmm);
  if (!m) throw new Error(`core-nf525: heure de bascule 'HH:MM' attendue (${hhmm})`);
  return parseInt(m[1] as string, 10) * 60 + parseInt(m[2] as string, 10);
}

/** Minutes depuis minuit → 'HH:MM' (inverse exact de parseDayCutoff). */
export function formatDayCutoff(minutes: number): string {
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1439) {
    throw new Error(`core-nf525: bascule en minutes 0..1439 attendue (${minutes})`);
  }
  return `${String(Math.trunc(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Veille d'une date AAAAMMJJ (calendrier grégorien réel — miroir de `nextDay`). */
export function prevDay(dayYmd: string): string {
  if (!DAY_RE.test(dayYmd)) throw new Error(`core-nf525: jour AAAAMMJJ attendu (${dayYmd})`);
  const d = new Date(parseInt(dayYmd.slice(0, 4), 10), parseInt(dayYmd.slice(4, 6), 10) - 1, parseInt(dayYmd.slice(6, 8), 10));
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Journée FISCALE (AAAAMMJJ) d'un horodatage fiscal AAAAMMJJHHMMSS, pour une bascule donnée
 * (minutes depuis minuit). Bascule 0 = jour calendaire (identité). Heure < bascule → journée de
 * la VEILLE (la nuit prolongée appartient à la journée qui l'a commencée).
 */
export function fiscalDayOf(ymdhms: string, cutoffMinutes: number): string {
  if (!YMDHMS_RE.test(ymdhms)) throw new Error(`core-nf525: horodatage AAAAMMJJHHMMSS attendu (${ymdhms})`);
  if (!Number.isInteger(cutoffMinutes) || cutoffMinutes < 0 || cutoffMinutes > 1439) {
    throw new Error(`core-nf525: bascule en minutes 0..1439 attendue (${cutoffMinutes})`);
  }
  const day = ymdhms.slice(0, 8);
  if (cutoffMinutes === 0) return day; // jour calendaire — aucun décalage
  const minutes = parseInt(ymdhms.slice(8, 10), 10) * 60 + parseInt(ymdhms.slice(10, 12), 10);
  return minutes < cutoffMinutes ? prevDay(day) : day;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// LES HORAIRES DU MAGASIN COMMANDENT LA BASCULE (décision utilisateur 2026-09-07, maquette §Magasin).
//
// POURQUOI DÉRIVER PLUTÔT QUE SAISIR. La bascule et l'heure de fermeture décrivent le MÊME fait —
// « quand la journée de vente s'achève ». Saisies séparément, elles divergent au premier oubli, et
// l'écart ne se voit nulle part : la caisse clôture à minuit pendant qu'un magasin vend encore, ou
// une journée fiscale reste ouverte des heures après la fermeture. UN seul réglage saisi (les
// horaires), UNE règle de dérivation, et le commerçant n'a plus qu'une chose à comprendre.
//
// ⚠️ LA RÈGLE, ÉCRITE ICI ET NULLE PART AILLEURS. Le back-office, le serveur et la caisse
// l'appellent — deux implémentations « à peu près pareilles » attribueraient les pièces de fin de
// nuit à deux journées différentes selon qui calcule (Forme A, mais sur du fiscal).
// ══════════════════════════════════════════════════════════════════════════════════════════════

/** 'HH:MM' valide ? Sert aux portes qui refusent une saisie AVANT de la stocker. */
export function isDayTime(hhmm: string): boolean {
  return HHMM_RE.test(hhmm);
}

/**
 * LA BASCULE, DÉRIVÉE DES HORAIRES — 'HH:MM' → 'HH:MM'.
 *
 *   · fermeture APRÈS minuit (close < open) → **l'heure de fermeture elle-même** (ferme 02:00 →
 *     bascule 02:00) ;
 *   · sinon → 00:00, la convention comptable.
 *
 * ⚠️ EXACTEMENT L'HEURE DE FERMETURE, SANS MARGE (décision utilisateur 2026-09-07 : « si je mets
 * 02h00 en clôture, je veux 02h00 en bascule »). J'avais d'abord posé l'heure pleine SUIVANTE pour
 * laisser une marge à la dernière vente ; la règle simple l'emporte, et elle est défendable : la
 * bascule est l'heure à laquelle le magasin ferme, point. Le prix à connaître : un encaissement
 * APRÈS l'heure déclarée (02:03 sur une fermeture à 02:00) tombe sur la journée SUIVANTE. C'est
 * cohérent — le magasin est censé être fermé — mais un commerce qui déborde régulièrement doit
 * déclarer l'heure à laquelle il ferme VRAIMENT, pas celle de son enseigne.
 *
 * ⚠️ UNE FERMETURE DE SOIRÉE RETOMBE SUR 00:00. Fermer à 23:30 n'est pas une nocturne (close > open) :
 * la journée fiscale suit alors le calendrier, ce qui est la convention comptable.
 */
export function deriveDayCutoff(open: string, close: string): string {
  if (!isDayTime(open) || !isDayTime(close)) {
    throw new Error(`core-nf525: horaires 'HH:MM' attendus (${open} → ${close})`);
  }
  const o = parseDayCutoff(open), c = parseDayCutoff(close);
  // Journée « normale » (ferme le jour même) : la journée fiscale suit le calendrier.
  if (c >= o) return '00:00';
  return formatDayCutoff(c);
}

/**
 * LA BASCULE QUI S'APPLIQUE MAINTENANT, une pending pouvant attendre son heure.
 *
 * ⚠️ C'EST LE VERROU NF525, ET IL EST SANS TÂCHE DE FOND. Changer la bascule d'une journée EN COURS
 * réattribuerait les pièces déjà scellées : deux journées se chevaucheraient, ou une seconde
 * resterait orpheline. La nouvelle valeur porte donc le moment où elle prend effet — la prochaine
 * frontière de journée, c'est-à-dire le prochain Z — et TOUT LECTEUR résout la même chose au même
 * instant. Aucun promoteur à faire tourner, donc aucun promoteur à surveiller ni à réparer.
 *
 * @param effectiveAt horodatage fiscal AAAAMMJJHHMMSS à partir duquel `pending` fait foi.
 */
export function resolveDayCutoff(
  current: string, pending: string | null, effectiveAt: string | null, nowYmdHms: string,
): string {
  if (!pending || !effectiveAt) return current;
  if (!YMDHMS_RE.test(effectiveAt) || !YMDHMS_RE.test(nowYmdHms)) return current;
  return nowYmdHms >= effectiveAt ? pending : current;
}

/**
 * LE DÉBUT DE LA JOURNÉE FISCALE EN COURS (AAAAMMJJHHMMSS) — la borne de ce qui appartient « au jour » : le journal des
 * ventes de la caisse, comme la clôture journalière.
 * ⚠️ PAS MINUIT : avec une bascule à 02:00, à 00:30 la journée a commencé la VEILLE à 02:00 — couper au minuit calendaire
 *    retirait du journal les ventes de la soirée, que la clôture et l'annulation traitent comme ventes du jour.
 */
export function fiscalDayStart(nowYmdHms: string, cutoffMinutes: number): string {
  const jour = fiscalDayOf(nowYmdHms, cutoffMinutes);
  return `${jour}${formatDayCutoff(cutoffMinutes).replace(':', '')}00`;
}

/**
 * LE DÉBUT DE LA PROCHAINE JOURNÉE FISCALE — quand une bascule changée prendra effet.
 *
 * ⚠️ CALCULÉ AVEC LA BASCULE **EN VIGUEUR**, jamais avec la nouvelle : c'est la journée EN COURS
 * qu'il s'agit de laisser finir intacte. Prendre la nouvelle déplacerait la frontière qu'on cherche
 * précisément à ne pas bouger.
 */
export function nextFiscalDayStart(nowYmdHms: string, currentCutoffMinutes: number): string {
  // ⚠️ `nextDay` VIENT DE `grand-totals`, il n'est PAS réécrit ici — j'en avais posé une copie
  //    privée, c'est-à-dire une seconde définition du lendemain dans le même paquet fiscal. Deux
  //    calendriers qui divergeraient d'un jour bissextile suffiraient à décaler une échéance de
  //    bascule (Forme A).
  const jour = fiscalDayOf(nowYmdHms, currentCutoffMinutes);
  const hhmm = formatDayCutoff(currentCutoffMinutes).replace(':', '');
  return `${nextDay(jour)}${hhmm}00`;
}
